import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CellContext } from "@tanstack/react-table";
import ClampLines from "react-clamp-lines";
import { AssignmentIcon, WarningIcon } from "components";
import {
  useGetPatients,
  useCurrentUser,
  useGetOrganizationUsers,
} from "utils/hooks";
import {
  useOrganizationSettings,
  getPatientWordCapitalized,
} from "utils/hooks/organizations/useOrganizationSettings";
import {
  getDate,
  getTherapistName,
  getTotalLabel,
  getPatientNameEmail,
} from "./helpers";

import { AssignmentSubmission } from "types";

type Props = {
  submissionsTotal: number;
  tabKey?: string;
};

const useInboxDataColumns = ({ submissionsTotal, tabKey }: Props) => {
  const { t } = useTranslation(["common"]);
  const { user } = useCurrentUser();
  const { settings: orgSettings } = useOrganizationSettings();
  const { organizationUsers } = useGetOrganizationUsers({
    organizationId: user?.activeOrganizationId || null,
  });
  const { patients } = useGetPatients({});

  const inboxDataColunms = useMemo(() => {
    const columns = [
      {
        header: t("inbox.table_header_assignment"),
        accessorKey: "assignment_label",
        cell: (props: CellContext<AssignmentSubmission, any>) => {
          return (
            <div className="flex align-items-center inbox-table_label">
              {props.row.original.isFlagged ? (
                <WarningIcon variant="medium" />
              ) : (
                <AssignmentIcon
                  style={props.row.original.assignmentLabel}
                  variant="medium"
                  color={props.row.original.color}
                  icon={props.row.original.icon?.src}
                />
              )}
              <div>{props.row.original.assignmentLabel}</div>
            </div>
          );
        },
        footer: () =>
          tabKey ? (
            <div className="inbox-table_total">
              {submissionsTotal} {getTotalLabel(tabKey)}
            </div>
          ) : undefined,
      },
      {
        header: t("inbox.table_header_patient", {
          patientWordCapitalized: getPatientWordCapitalized(orgSettings),
        }),
        cell: (props: CellContext<AssignmentSubmission, any>) => {
          const patientData = getPatientNameEmail(
            props.row.original.userId,
            patients
          );
          const patientName = props.row.original.userName || patientData?.name;
          return (
            <>
              {patientName && (
                <div className="inbox-table_patient-name">{patientName}</div>
              )}
              <div className="inbox-table_patient-email">
                {props.row.original.userEmail ||
                  patientData?.email ||
                  props.row.original.userNameOrEmail ||
                  ""}
              </div>
            </>
          );
        },
        footer: "",
      },
      {
        header: t("inbox.table_header_date"),
        cell: (props: CellContext<AssignmentSubmission, string>) => {
          const date = getDate(props.row.original.createdAt);
          return <div>{date ? date : ""}</div>;
        },
        footer: "",
      },
      {
        header: t("inbox.table_header_group"),
        cell: (props: CellContext<AssignmentSubmission, string>) => {
          const groupName = props.row.original.groupName || "";
          return (
            <div className="inbox-table_group-name">
              <ClampLines
                text={groupName}
                id="group-name-clamped"
                lines={1}
                ellipsis="..."
                buttons={false}
                innerElement="div"
              />
            </div>
          );
        },
        footer: "",
      },
    ];

    if (tabKey !== "unassigned") {
      columns.push({
        header: t("inbox.table_header_therapist"),
        cell: (props: CellContext<AssignmentSubmission, string>) => {
          return (
            <div>
              {getTherapistName(
                props.row.original.therapistId,
                organizationUsers
              )}
            </div>
          );
        },
        footer: "",
      });
    }

    return columns;
  }, [t, submissionsTotal, tabKey, patients, organizationUsers, orgSettings]);

  return inboxDataColunms;
};

export default useInboxDataColumns;
